import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router';
import AgentLayout from '../../components/agent/layout/AgentLayout';
import AgentConversationStarter from '../../components/agent/home/AgentConversationStarter';
import AgentUserMessageInput from '../../components/agent/input/AgentUserMessageInput';
import { ICON_BUTTON_ICONS } from '../../components/buttons/IconButton';
import { paths, routes } from '../../utils/constants/routes';
import { MAX_SIDEBAR_RECENT_CHATS, AI_DISCLAIMER_TEXT } from '../../utils/constants/agent';
import { useAgentSession, useAgentSessionList } from '../../components/agent/hooks';
import useSubscriptionStatus from '../../hooks/useSubscriptionStatus';
import { useConfirmDialog } from '../../components/dialogs/ConfirmDialog';
import {
  eventAgenticAiOpened,
  eventAgenticAiQuickActionClicked,
  eventAgenticAiConversationOpened,
  eventAgenticAiConversationFavorited,
  eventAgenticAiConversationArchived,
  eventAgenticAiQuotaExhausted
} from '../../ga4/ga4';

const getHomeInputPlaceholder = ({ tokensExhausted, isLoadingRecentChats }) => {
  if (tokensExhausted) return 'AI features on hold — contact sales';
  if (isLoadingRecentChats) return 'Loading agent...';
  return 'Type your message here...';
};

// 🔥 CONVERTED TO PURE REACT - NO observer() wrapper needed!
const Agent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const store = useAgentSession();
  const {
    allSessions,
    updateSession,
    deleteSession,
    isLoading: isLoadingRecentChats
  } = useAgentSessionList();
  // TD-2119 R3: gate the home-page chat input on total-pool exhaustion.
  const { tokens } = useSubscriptionStatus();
  const tokensExhausted = tokens?.totalExhausted === true;
  const { showConfirm } = useConfirmDialog();

  // Keyboard shortcut for search (Cmd/Ctrl+K)
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Check for Cmd+K (Mac) or Ctrl+K (Windows/Linux)
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        handleSearchConversations();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Analytics: agent opened (once per mount).
  useEffect(() => {
    eventAgenticAiOpened();
  }, []);

  // Analytics: token quota exhausted — fire once when the pool becomes exhausted.
  const quotaExhaustedFiredRef = useRef(false);
  useEffect(() => {
    if (tokensExhausted && !quotaExhaustedFiredRef.current) {
      quotaExhaustedFiredRef.current = true;
      eventAgenticAiQuotaExhausted();
    } else if (!tokensExhausted) {
      quotaExhaustedFiredRef.current = false;
    }
  }, [tokensExhausted]);

  // Sidebar handlers
  const handleNewChat = () => {
    // Navigate to agent welcome page (essentially a reset to new chat state)
    navigate(paths.AGENT);
  };

  const handleSearchConversations = () => {
    // Navigate to all conversations page for search
    navigate(paths.AGENT_CONVERSATIONS);
  };

  const handleBrowseByProject = () => {
    if (import.meta.env.DEV) console.log('Browse by project clicked');
    // TODO: Implement browse by project functionality
  };

  const handleFavorites = () => {
    navigate(paths.AGENT_FAVORITES);
  };

  const handleArchived = () => {
    navigate(paths.AGENT_ARCHIVED);
  };

  const handleShowMoreRecentChats = () => {
    navigate(paths.AGENT_CONVERSATIONS);
  };

  const handleChatClick = (chatId) => {
    eventAgenticAiConversationOpened(chatId);
    // Navigate to specific chat
    navigate(routes.AGENT_CHAT_PATH.replace(':chatId', chatId));
  };

  // Session context menu handlers
  /**
   * Rename a chat session. Supports both inline editing (when newName is provided)
   * and legacy context menu (shows prompt dialog when newName is omitted).
   *
   * @param {string} chatId - The session ID to rename
   * @param {string} [newName] - Optional new name. If provided, skips prompt dialog.
   *                             If omitted, shows prompt dialog for user input.
   * @returns {Promise<void>}
   */
  const handleRenameChat = async (chatId, newName) => {
    const session = allSessions.find((s) => s.id === chatId);
    if (!session) return;

    // If newName not provided (legacy context menu without inline edit), show prompt
    if (!newName) {
      newName = prompt('Enter new name for this conversation:', session.title);
    }

    if (newName && newName.trim() !== '') {
      try {
        await updateSession(chatId, { name: newName.trim() });
        if (import.meta.env.DEV) console.log('✅ Chat renamed successfully:', chatId);
      } catch (error) {
        console.error('❌ Failed to rename chat:', error);
        alert('Failed to rename conversation. Please try again.');
      }
    }
  };

  const handleToggleChatFavorite = async (chatId) => {
    const session = allSessions.find((s) => s.id === chatId);
    if (!session) return;

    if (!session.isFavorite) {
      eventAgenticAiConversationFavorited(chatId);
    }

    try {
      await updateSession(chatId, { is_favorite: !session.isFavorite });
      if (import.meta.env.DEV) console.log('✅ Chat favorite toggled:', chatId);
    } catch (error) {
      console.error('❌ Failed to toggle favorite:', error);
      alert('Failed to update favorite status. Please try again.');
    }
  };

  const handleArchiveChat = async (chatId) => {
    const session = allSessions.find((s) => s.id === chatId);
    if (!session) return;

    eventAgenticAiConversationArchived(chatId);

    try {
      await updateSession(chatId, { is_archived: true });
      if (import.meta.env.DEV) console.log('✅ Chat archived:', chatId);
    } catch (error) {
      console.error('❌ Failed to archive chat:', error);
      alert('Failed to archive conversation. Please try again.');
    }
  };

  const handleDeleteChat = async (chatId) => {
    const session = allSessions.find((s) => s.id === chatId);
    if (!session) return;

    const confirmed = await showConfirm({
      title: 'Delete Conversation',
      message: `Are you sure you want to delete "${session.title}"? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'danger'
    });

    if (confirmed) {
      try {
        await deleteSession(chatId);
        if (import.meta.env.DEV) console.log('✅ Chat deleted:', chatId);
      } catch (error) {
        console.error('❌ Failed to delete chat:', error);
        alert('Failed to delete conversation. Please try again.');
      }
    }
  };

  // Conversation starter handlers
  const handleCreateCompanyShortlist = () => {
    eventAgenticAiQuickActionClicked('create_shortlist');
    const predefinedMessage =
      'Create a shortlist of companies for me. Use the characteristics search. Guide me through choosing the industry, value chain position, product focus, and financial criteria, then present the results.';
    handleChatSubmit(predefinedMessage);
  };

  const handleExploreCompaniesByIndustry = () => {
    eventAgenticAiQuickActionClicked('explore_by_industry');
    const predefinedMessage =
      'Help me explore companies by industry. Guide me to select one or more industry categories, drill into subcategories, and show me which firms are active in each area.';
    handleChatSubmit(predefinedMessage);
  };

  const handleFindCompanyByName = () => {
    eventAgenticAiQuickActionClicked('find_by_name');
    const predefinedMessage =
      'Find a company by name. Guide me to find the right legal entity, then provide a detailed profile.';
    handleChatSubmit(predefinedMessage);
  };

  const handleContinueSavedSearch = () => {
    eventAgenticAiQuickActionClicked('continue_saved');
    const predefinedMessage =
      'List my 10 most recent projects. Help me select the right one and then guide me through refining filters, updating the shortlist, or exporting the results.';
    handleChatSubmit(predefinedMessage);
  };

  // Chat input handlers
  const submitNewChat = async (message) => {
    if (import.meta.env.DEV) {
      console.log('🔍 DEBUG AGENT WELCOME: =========================');
      console.log('🔍 DEBUG AGENT WELCOME: handleChatSubmit called with message:', message);
      console.log('🔍 DEBUG AGENT WELCOME: Timestamp:', new Date().toISOString());
    }

    try {
      if (import.meta.env.DEV) {
        console.log('🔍 DEBUG AGENT WELCOME: Store state BEFORE createSession:', {
          agentState: store.agentState,
          connectionStatus: store.connectionStatus,
          currentSessionId: store.currentSessionId,
          sessionCount: store.sessionIds?.length || 0,
          isLoadingSession: store.isLoadingSession,
          isSendingMessage: store.isSendingMessage,
          error: store.error
        });
        console.log('🔍 DEBUG AGENT WELCOME: Calling store.createSession...');
      }
      const startTime = Date.now();

      // Create new session with initial message (auto-sends and gets mock response)
      const session = await store.createSession(message);

      if (import.meta.env.DEV) {
        const endTime = Date.now();
        console.log(
          '🔍 DEBUG AGENT WELCOME: createSession completed in:',
          endTime - startTime + 'ms'
        );
        console.log('🔍 DEBUG AGENT WELCOME: Session created:', {
          sessionId: session.id,
          title: session.title,
          createdAt: session.createdAt,
          isActive: session.isActive
        });
        console.log('🔍 DEBUG AGENT WELCOME: Store state AFTER createSession:', {
          agentState: store.agentState,
          connectionStatus: store.connectionStatus,
          currentSessionId: store.currentSessionId,
          sessionCount: store.sessionIds?.length || 0,
          isLoadingSession: store.isLoadingSession,
          isSendingMessage: store.isSendingMessage,
          error: store.error
        });

        const sessionMessages = store.getMessages(session.id);
        console.log('🔍 DEBUG AGENT WELCOME: Messages in created session:', {
          messageCount: sessionMessages.length,
          messages: sessionMessages.map((m) => ({
            id: m.id,
            type: m.type,
            content: m.content.substring(0, 50) + '...'
          }))
        });
      }

      // Navigation debug
      const navigationPath = routes.AGENT_CHAT_PATH.replace(':chatId', session.id);
      if (import.meta.env.DEV) {
        console.log('🔍 DEBUG AGENT WELCOME: About to navigate to:', navigationPath);
      }

      // Navigate to the new chat session
      navigate(navigationPath);

      if (import.meta.env.DEV) {
        console.log(
          '🔍 DEBUG AGENT WELCOME: Navigation called - React Router should handle redirect'
        );
        console.log('🔍 DEBUG AGENT WELCOME: =========================');
      }
    } catch (error) {
      console.error('🔍 DEBUG AGENT WELCOME: ERROR in handleChatSubmit:', {
        error: error.message,
        stack: error.stack,
        timestamp: new Date().toISOString(),
        storeState: {
          agentState: store.agentState,
          connectionStatus: store.connectionStatus,
          error: store.error
        }
      });
    }
  };

  // Public entry point for the input + conversation starters. Concurrent-session
  // enforcement now happens at login (TD-2239), not per send.
  const handleChatSubmit = (message) => {
    submitNewChat(message);
  };

  const handleChatUpload = () => {
    if (import.meta.env.DEV) console.log('Upload clicked');
    // TODO: Implement file upload functionality
  };

  // Get recent chats from agent session store
  // Always show non-archived sessions (no filter toggle)
  const recentChats = (() => {
    const sessions = allSessions || [];

    // Filter: exclude archived sessions by default
    const nonArchivedSessions = sessions.filter((s) => !s.isArchived);

    // Take first MAX_SIDEBAR_RECENT_CHATS and map to expected format
    return nonArchivedSessions.slice(0, MAX_SIDEBAR_RECENT_CHATS).map((s) => ({
      id: s.id,
      label: s.title,
      isFavorite: s.isFavorite || false,
      isArchived: s.isArchived || false
    }));
  })();

  return (
    <AgentLayout
      onNewChat={handleNewChat}
      onSearchConversations={handleSearchConversations}
      onBrowseByProject={handleBrowseByProject}
      onFavorites={handleFavorites}
      onArchived={handleArchived}
      onShowMoreRecentChats={handleShowMoreRecentChats}
      onChatClick={handleChatClick}
      onRenameChat={handleRenameChat}
      onToggleChatFavorite={handleToggleChatFavorite}
      onArchiveChat={handleArchiveChat}
      onDeleteChat={handleDeleteChat}
      recentChats={recentChats}
      isLoadingRecentChats={isLoadingRecentChats}
      activeRoute={location.pathname}
      showChatInput={false}
    >
      <div className="agent-home-page">
        <div className="agent-home-content">
          <img src="/icons/agent/agent-icon.png" alt="Agent Icon" className="agent-home-icon" />

          <h1 className="agent-home-title">How can I help you today?</h1>

          <div className="agent-inquiry-examples">
            <AgentConversationStarter
              icon={ICON_BUTTON_ICONS.SEARCH}
              title="Create Company Shortlist"
              description="Identify companies based on their industry, value chain positioning, product focus, and financial criteria."
              onClick={handleCreateCompanyShortlist}
              disabled={isLoadingRecentChats || tokensExhausted}
            />
            <AgentConversationStarter
              icon={ICON_BUTTON_ICONS.BUILDING}
              title="Explore Companies by Industry"
              description="Search for companies by selecting broader industry categories and see which firms are active in each area."
              onClick={handleExploreCompaniesByIndustry}
              disabled={isLoadingRecentChats || tokensExhausted}
            />
            <AgentConversationStarter
              icon={ICON_BUTTON_ICONS.CARD}
              title="Find a Company by Name"
              description="Quickly look up companies by name to view detailed profiles and add them to your Watchlist."
              onClick={handleFindCompanyByName}
              disabled={isLoadingRecentChats || tokensExhausted}
            />
            <AgentConversationStarter
              icon={ICON_BUTTON_ICONS.FILE_EARMARK_TEXT}
              title="Continue a Saved Search"
              description="Access your saved searches to refine filters, shortlist companies, or export results."
              onClick={handleContinueSavedSearch}
              disabled={isLoadingRecentChats || tokensExhausted}
            />
          </div>

          <div className="agent-home-input">
            <AgentUserMessageInput
              onSubmit={handleChatSubmit}
              onUpload={handleChatUpload}
              placeholder={getHomeInputPlaceholder({
                tokensExhausted,
                isLoadingRecentChats
              })}
              disabled={isLoadingRecentChats || tokensExhausted}
              autoFocus={true}
            />
            {/* TD-2325: Same AI disclaimer shown below the input on active chats.
                <output> carries an implicit status role for screen readers. */}
            <output className="agent-ai-disclaimer" aria-label="AI disclaimer notice">
              {AI_DISCLAIMER_TEXT}
            </output>
          </div>
        </div>
      </div>
    </AgentLayout>
  );
};

export default Agent;
